import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import VerifiedBadge from '../../components/aisle/VerifiedBadge'
import AvailabilityCalendar from '../../components/aisle/AvailabilityCalendar'
import { Wallet, Inbox, Store, LogOut } from 'lucide-react'

const CATEGORY_LABELS = {
  photographer:'Photographer', videographer:'Videographer', decorator:'Decorator',
  mc_dj:'MC / DJ', caterer:'Caterer', aso_ebi:'Aso Ebi', makeup:'Makeup Artist',
  bridal_fashion:'Bridal Fashion', venue:'Venue', live_band:'Live Band',
  cake:'Cake Designer', planner:'Event Planner',
}
const fmt = n => `₦${Number(n || 0).toLocaleString()}`
const STATUS_LABEL = { inquiry:'Inquiry', confirmed:'Confirmed', completed:'Completed', cancelled:'Cancelled' }

const inputStyle = { width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.875rem', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.6rem 0.9rem', outline:'none', boxSizing:'border-box' }
const Label = ({ children }) => <label style={{ display:'block', fontFamily:'DM Sans, sans-serif', fontWeight:600, fontSize:'0.78rem', color:'#1A1A1A', margin:'0 0 0.3rem' }}>{children}</label>

export default function VendorDashboard() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [vendor, setVendor]   = useState(undefined) // undefined=loading, null=none
  const [leads, setLeads]     = useState([])
  const [edit, setEdit]       = useState({ description:'', vendor_vow:'', starting_price:'' })
  const [saving, setSaving]   = useState(false)
  const [savedMsg, setSavedMsg] = useState('')
  const [counterFor, setCounterFor] = useState(null)
  const [counterAmt, setCounterAmt] = useState('')
  const [blocked, setBlocked] = useState(new Set())
  const profileChannelRef = useRef(null)

  useEffect(() => {
    if (!user?.id) return
    supabase.from('vendors').select('*').eq('owner_user_id', user.id).order('created_at', { ascending: true }).limit(1).maybeSingle()
      .then(({ data }) => {
        setVendor(data || null)
        if (data) setEdit({ description: data.description || '', vendor_vow: data.vendor_vow || '', starting_price: data.starting_price || '' })
      }, () => setVendor(null))
    supabase.rpc('aisle_vendor_leads').then(({ data }) => setLeads(data || []), () => {})
  }, [user?.id])

  useEffect(() => {
    if (!vendor?.id) return
    supabase.from('vendor_availability').select('day').eq('vendor_id', vendor.id)
      .then(({ data }) => setBlocked(new Set((data || []).map(r => r.day))), () => {})
  }, [vendor?.id])

  // Live: new requests / secured bookings appear without a refresh
  useEffect(() => {
    if (!vendor?.id) return
    const ch = supabase.channel(`vendor-leads-${vendor.id}`)
      .on('postgres_changes', { event:'*', schema:'public', table:'vendor_bookings', filter:`vendor_id=eq.${vendor.id}` },
        () => supabase.rpc('aisle_vendor_leads').then(({ data }) => setLeads(data || []), () => {}))
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [vendor?.id])

  // Broadcast channel — lets couples viewing this profile refetch on save.
  // (vendors is not publicly readable, so postgres_changes can't deliver edits;
  //  Broadcast is RLS-free and the couple refetches via the public RPC.)
  useEffect(() => {
    if (!vendor?.id) return
    const ch = supabase.channel(`aisle-vendor-${vendor.id}`)
    ch.subscribe()
    profileChannelRef.current = ch
    return () => { supabase.removeChannel(ch); profileChannelRef.current = null }
  }, [vendor?.id])

  async function toggleDay(dStr) {
    if (!vendor?.id) return
    const has = blocked.has(dStr)
    setBlocked(prev => { const n = new Set(prev); has ? n.delete(dStr) : n.add(dStr); return n })
    try {
      if (has) await supabase.from('vendor_availability').delete().eq('vendor_id', vendor.id).eq('day', dStr)
      else     await supabase.from('vendor_availability').insert({ vendor_id: vendor.id, day: dStr })
    } catch { /* keep optimistic; will resync on reload */ }
  }

  async function saveListing() {
    if (!vendor) return
    setSaving(true); setSavedMsg('')
    try {
      await supabase.from('vendors').update({
        description: edit.description.trim() || null,
        vendor_vow: edit.vendor_vow.trim() || null,
        starting_price: Number(edit.starting_price) || null,
      }).eq('id', vendor.id)
      profileChannelRef.current?.send({ type: 'broadcast', event: 'updated', payload: {} })
      setSavedMsg('Saved ✓')
      setTimeout(() => setSavedMsg(''), 2500)
    } catch { setSavedMsg('Could not save') }
    setSaving(false)
  }

  async function respond(l, response, amount) {
    try {
      await supabase.rpc('aisle_vendor_respond', {
        p_booking_id: l.booking_id, p_response: response,
        p_counter_amount: amount ? Number(amount) : null, p_note: null,
      })
      setLeads(prev => prev.map(x => x.booking_id === l.booking_id
        ? { ...x, vendor_response: response, counter_amount: response === 'countered' ? Number(amount) : null, status: response === 'declined' ? 'cancelled' : x.status }
        : x))
    } catch { /* ignore */ }
    setCounterFor(null); setCounterAmt('')
  }

  if (vendor === undefined) return <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#FAFAF8', fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>Loading…</div>

  // Not a vendor → empty state
  if (vendor === null) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center', padding:'2rem 1rem', textAlign:'center' }}>
      <div className="card" style={{ maxWidth:440, width:'100%', padding:'2.5rem' }}>
        <Store size={36} color="#C4826A" style={{ margin:'0 auto 1rem', display:'block' }} />
        <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.4rem', margin:'0 0 0.6rem' }}>No vendor listing yet</h2>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.9rem', color:'#6B6B6B', lineHeight:1.7, margin:'0 0 1.5rem' }}>
          This account isn't linked to a vendor listing. Apply to join Okoiya Aisle and your dashboard will appear here.
        </p>
        <Link to="/aisle/become-a-vendor" style={{ fontFamily:'DM Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', color:'#fff', background:'#C4826A', borderRadius:100, padding:'0.7rem 1.4rem', textDecoration:'none' }}>Apply as a vendor</Link>
        <div><Link to="/dashboard" style={{ display:'inline-block', marginTop:'1rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#9B9B9B', textDecoration:'none' }}>← Back to Okoiya</Link></div>
      </div>
    </div>
  )

  const cover = (Array.isArray(vendor.gallery_images) && vendor.gallery_images[0]) || vendor.profile_image_url
  const paidBookings = leads.filter(l => l.booking_fee_paid)
  const expectedDeposits = leads.reduce((s, l) => s + Number(l.deposit_amount || 0), 0)

  return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', padding:'2rem 1rem' }}>
      <div style={{ maxWidth:880, margin:'0 auto' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1.75rem', flexWrap:'wrap', gap:'1rem' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'0.875rem' }}>
            <img src="/okoiya-logo.png" alt="Okoiya" style={{ height:34 }} />
            <div>
              <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.5rem', margin:0 }}>Vendor Dashboard</h1>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:0 }}>{vendor.business_name}</p>
            </div>
          </div>
          <button onClick={() => { signOut?.(); navigate('/') }} className="btn-secondary" style={{ fontSize:'0.8rem', padding:'0.55rem 1rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
            <LogOut size={14}/> Sign out
          </button>
        </div>

        {/* Stat cards */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:'0.75rem', marginBottom:'2rem' }}>
          {[
            { label:'Verification', value: vendor.verification_status === 'verified' ? 'Verified' : vendor.verification_status === 'pending' ? 'Pending' : 'Rejected', icon:Store },
            { label:'Total leads', value: leads.length, icon:Inbox },
            { label:'Confirmed bookings', value: paidBookings.length, icon:Inbox },
            { label:'Expected deposits', value: fmt(expectedDeposits), icon:Wallet },
          ].map(s => (
            <div key={s.label} className="card" style={{ padding:'1rem', display:'flex', alignItems:'center', gap:'0.75rem' }}>
              <s.icon size={18} color="#C4826A"/>
              <div>
                <p style={{ fontFamily:'Gibs, serif', fontSize:'1.15rem', margin:0, fontWeight:400 }}>{s.value}</p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color:'#6B6B6B', margin:0, fontWeight:600 }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Listing */}
        <div className="card" style={{ padding:'1.5rem', marginBottom:'1.5rem' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'1rem' }}>
            <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.15rem', margin:0 }}>Your listing</h2>
            <VerifiedBadge status={vendor.verification_status} size={18} />
          </div>
          {cover && <img src={cover} alt="" style={{ width:'100%', maxHeight:200, objectFit:'cover', borderRadius:'0.875rem', marginBottom:'1rem' }} />}
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 1rem' }}>
            {CATEGORY_LABELS[vendor.category] || vendor.category} · {vendor.city || '—'}
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
            <div><Label>Description</Label><textarea rows={2} style={{ ...inputStyle, resize:'vertical' }} value={edit.description} onChange={e=>setEdit(p=>({...p, description:e.target.value}))} /></div>
            <div><Label>Vendor Vow</Label><textarea rows={2} style={{ ...inputStyle, resize:'vertical' }} value={edit.vendor_vow} onChange={e=>setEdit(p=>({...p, vendor_vow:e.target.value}))} /></div>
            <div style={{ maxWidth:200 }}><Label>Starting price (₦)</Label><input type="number" style={inputStyle} value={edit.starting_price} onChange={e=>setEdit(p=>({...p, starting_price:e.target.value}))} /></div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'1rem', marginTop:'1rem' }}>
            <button onClick={saveListing} disabled={saving}
              style={{ fontFamily:'DM Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'#fff', background: saving?'#D4C4BC':'#C4826A', border:'none', borderRadius:100, padding:'0.6rem 1.4rem', cursor: saving?'default':'pointer' }}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {savedMsg && <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#3D8B5E', fontWeight:600 }}>{savedMsg}</span>}
          </div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.875rem 0 0' }}>
            Photos, verification status and your tier are managed by the Okoiya team.
          </p>
        </div>

        {/* Availability calendar */}
        <div className="card" style={{ padding:'1.5rem', marginBottom:'1.5rem' }}>
          <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.15rem', margin:'0 0 0.4rem' }}>Availability</h2>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0 0 1rem' }}>Tap a date to block it. Blocked dates can't be booked by couples — and confirmed bookings block automatically.</p>
          <AvailabilityCalendar blocked={blocked} onToggle={toggleDay} />
        </div>

        {/* Leads */}
        <div className="card" style={{ padding:'1.5rem' }}>
          <h2 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.15rem', margin:'0 0 1rem' }}>Leads &amp; bookings</h2>
          {leads.length === 0 ? (
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#BCBCBC', textAlign:'center', padding:'1.5rem' }}>No leads yet. They'll appear here when couples book you.</p>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.6rem' }}>
              {leads.map(l => (
                <div key={l.booking_id} style={{ background:'#FAFAF8', border:'1px solid #F0EDE7', borderRadius:'0.75rem', padding:'0.875rem 1rem' }}>
                  <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'0.75rem' }}>
                    <div style={{ minWidth:0 }}>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.85rem', margin:0 }}>{l.couple_name || 'A couple'}</p>
                      <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#9B9B9B', margin:'0.1rem 0 0' }}>
                        {l.event_date ? `Event ${new Date(l.event_date).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })} · ` : ''}
                        requested {new Date(l.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short' })}
                        {l.deposit_amount ? ` · deposit ${fmt(l.deposit_amount)}` : ''}
                      </p>
                    </div>
                    {l.booking_fee_paid && (
                      <span style={{ fontFamily:'DM Sans, sans-serif', fontSize:'0.66rem', fontWeight:700, flexShrink:0, color:'#3D8B5E', background:'rgba(61,139,94,0.1)', borderRadius:100, padding:'2px 9px' }}>Secured</span>
                    )}
                  </div>

                  <div style={{ marginTop:'0.6rem' }}>
                    {l.vendor_response === 'pending' ? (
                      counterFor === l.booking_id ? (
                        <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', alignItems:'center' }}>
                          <input value={counterAmt} onChange={e=>setCounterAmt(e.target.value)} type="number" placeholder="Counter ₦"
                            style={{ width:130, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', border:'1px solid #E5E0D8', borderRadius:100, padding:'0.4rem 0.8rem', outline:'none' }} />
                          <button onClick={()=>respond(l, 'countered', counterAmt)} disabled={!counterAmt}
                            style={{ fontFamily:'DM Sans, sans-serif', fontWeight:700, fontSize:'0.74rem', color:'#fff', background: counterAmt?'#B45309':'#D4C4BC', border:'none', borderRadius:100, padding:'0.4rem 0.9rem', cursor: counterAmt?'pointer':'default' }}>Send counter</button>
                          <button onClick={()=>{ setCounterFor(null); setCounterAmt('') }} style={{ fontFamily:'DM Sans, sans-serif', fontWeight:600, fontSize:'0.74rem', color:'#6B6B6B', background:'none', border:'none', cursor:'pointer' }}>Cancel</button>
                        </div>
                      ) : (
                        <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
                          <button onClick={()=>respond(l, 'accepted')} style={{ fontFamily:'DM Sans, sans-serif', fontWeight:700, fontSize:'0.74rem', color:'#fff', background:'#9BAF9B', border:'none', borderRadius:100, padding:'0.4rem 1rem', cursor:'pointer' }}>Accept</button>
                          <button onClick={()=>{ setCounterFor(l.booking_id); setCounterAmt('') }} style={{ fontFamily:'DM Sans, sans-serif', fontWeight:700, fontSize:'0.74rem', color:'#B45309', background:'#fff', border:'1.5px solid rgba(180,83,9,0.4)', borderRadius:100, padding:'0.4rem 1rem', cursor:'pointer' }}>Counter</button>
                          <button onClick={()=>respond(l, 'declined')} style={{ fontFamily:'DM Sans, sans-serif', fontWeight:700, fontSize:'0.74rem', color:'#B91C1C', background:'#fff', border:'1.5px solid rgba(220,38,38,0.3)', borderRadius:100, padding:'0.4rem 1rem', cursor:'pointer' }}>Decline</button>
                        </div>
                      )
                    ) : (
                      <span style={{ fontFamily:'DM Sans, sans-serif', fontSize:'0.72rem', fontWeight:700,
                        color: l.vendor_response==='accepted' ? '#3D8B5E' : l.vendor_response==='countered' ? '#B45309' : '#B91C1C' }}>
                        {l.vendor_response==='accepted' ? '✓ You accepted'
                          : l.vendor_response==='countered' ? `Countered ${fmt(l.counter_amount)}`
                          : 'Declined'}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'1rem 0 0' }}>
            Tip: accept or counter a request below — accepting auto-blocks that date on your calendar.
          </p>
        </div>

      </div>
    </div>
  )
}
